import type { ReactNode } from 'react';
import {
  Box,
  Divider,
  Flex,
  FormLayoutGroup,
  NumberField,
  Stack,
  Strong,
  Text,
  Toggle,
} from '@customerio/pluma-components/react';
import { formatCentsToUsd } from '@/utils/currency';
import {
  immediateTopUpChargeCents,
  refillCents,
  type AutoTopUpErrors,
  type AutoTopUpSettings,
} from '../utils/add-funds';
import * as styles from './add-funds.css';

export type AutoTopUpBoxProps = {
  settings: AutoTopUpSettings;
  errors: AutoTopUpErrors;
  balanceCents: number;
  isDisabled?: boolean;
  onChange: (settings: AutoTopUpSettings) => void;
};

export function AutoTopUpBox({ settings, errors, balanceCents, isDisabled, onChange }: AutoTopUpBoxProps) {
  const update = (patch: Partial<AutoTopUpSettings>) => onChange({ ...settings, ...patch });

  return (
    <Box
      data-test-auto-top-up-box
      className={settings.isEnabled ? `${styles.optionBox} ${styles.optionBoxActive}` : styles.optionBox}
    >
      <Stack gap="200">
        <ToggleRow
          title="Auto top-up"
          description="Refill automatically so sending never stops."
          isChecked={settings.isEnabled}
          isDisabled={isDisabled}
          onChange={(isEnabled) => update({ isEnabled })}
        />
        {settings.isEnabled ? (
          <>
            <Stack gap="100">
              <FormLayoutGroup gap="150">
                <DollarField
                  label="When balance reaches"
                  value={settings.triggerDollars}
                  isDisabled={isDisabled}
                  isInvalid={errors.trigger != null}
                  onChange={(triggerDollars) => update({ triggerDollars })}
                />
                <DollarField
                  label="Replenish to"
                  value={settings.replenishDollars}
                  isDisabled={isDisabled}
                  isInvalid={errors.replenish != null}
                  onChange={(replenishDollars) => update({ replenishDollars })}
                />
              </FormLayoutGroup>
              <RuleLine settings={settings} errors={errors} balanceCents={balanceCents} />
            </Stack>
            <Divider className={styles.activeDivider} />
            <ToggleRow
              title="Monthly limit"
              description="Stop refilling once this much has been charged in a month."
              isChecked={settings.isMonthlyLimitEnabled}
              isDisabled={isDisabled}
              onChange={(isMonthlyLimitEnabled) => update({ isMonthlyLimitEnabled })}
            />
            {settings.isMonthlyLimitEnabled ? (
              <MonthlyLimitField
                limitDollars={settings.monthlyLimitDollars}
                error={errors.monthlyLimit}
                isDisabled={isDisabled}
                onChange={(monthlyLimitDollars) => update({ monthlyLimitDollars })}
              />
            ) : null}
          </>
        ) : null}
      </Stack>
    </Box>
  );
}

type MonthlyLimitFieldProps = {
  limitDollars: number | null;
  error: string | undefined;
  isDisabled?: boolean;
  onChange: (limitDollars: number | null) => void;
};

function MonthlyLimitField({ limitDollars, error, isDisabled, onChange }: MonthlyLimitFieldProps) {
  return (
    <Stack gap="100">
      <Box style={{ maxWidth: '50%' }}>
        <DollarField
          label="Monthly limit amount"
          isLabelHidden={true}
          elementAfter={
            <Text color="secondary" size="sm" pr="150" whiteSpace="nowrap" flexShrink="0">
              per month
            </Text>
          }
          value={limitDollars}
          isDisabled={isDisabled}
          isInvalid={error != null}
          onChange={onChange}
        />
      </Box>
      {error == null ? (
        <Text color="secondary" size="sm">
          Refills stop once <Strong color="inherit">{dollars(limitDollars)}</Strong> has been charged this month.
        </Text>
      ) : (
        <Text color="critical" size="sm">
          {error}
        </Text>
      )}
    </Stack>
  );
}

type ToggleRowProps = {
  title: string;
  description: string;
  isChecked: boolean;
  isDisabled?: boolean;
  onChange: (isChecked: boolean) => void;
};

export function ToggleRow({ title, description, isChecked, isDisabled, onChange }: ToggleRowProps) {
  return (
    <Flex alignItems="flex-start" justifyContent="space-between" gap="200">
      <Stack gap="025">
        <Text weight="bold">{title}</Text>
        <Text color="secondary" size="sm">
          {description}
        </Text>
      </Stack>
      <Toggle
        ariaLabel={title}
        size="sm"
        isChecked={isChecked}
        isDisabled={isDisabled}
        flexShrink="0"
        onChange={(event) => onChange(event.currentTarget.checked)}
      />
    </Flex>
  );
}

function dollars(value: number | null): string {
  return formatCentsToUsd((value ?? 0) * 100);
}

type RuleLineProps = {
  settings: AutoTopUpSettings;
  errors: AutoTopUpErrors;
  balanceCents: number;
};

function RuleLine({ settings, errors, balanceCents }: RuleLineProps) {
  const error = errors.trigger ?? errors.replenish;
  if (error != null) {
    return (
      <Text data-test-auto-top-up-error color="critical" size="sm">
        {error}
      </Text>
    );
  }

  const trigger = <Strong color="inherit">{dollars(settings.triggerDollars)}</Strong>;
  const replenish = <Strong color="inherit">{dollars(settings.replenishDollars)}</Strong>;
  const chargeNowCents = immediateTopUpChargeCents(balanceCents, settings);

  let sentence: ReactNode;
  if (chargeNowCents > 0) {
    sentence = (
      <>
        Your balance {balanceCents < (settings.triggerDollars ?? 0) * 100 ? 'is below' : 'is at'} {trigger}, so we'll
        charge <Strong color="inherit">{formatCentsToUsd(chargeNowCents)}</Strong> right away to bring it to {replenish}
        .
      </>
    );
  } else {
    sentence = (
      <>
        When your balance reaches {trigger}, we'll charge your card{' '}
        <Strong color="inherit">{formatCentsToUsd(refillCents(settings))}</Strong> to bring it back to {replenish}.
      </>
    );
  }

  return (
    <Text data-test-auto-top-up-rule color="secondary" size="sm">
      {sentence}
    </Text>
  );
}

type DollarFieldProps = {
  label: string;
  isLabelHidden?: boolean;
  value: number | null;
  isInvalid: boolean;
  isDisabled?: boolean;
  elementAfter?: ReactNode;
  onChange: (value: number | null) => void;
};

export function DollarField({
  label,
  isLabelHidden,
  value,
  isInvalid,
  isDisabled,
  elementAfter,
  onChange,
}: DollarFieldProps) {
  return (
    <NumberField
      label={isLabelHidden === true ? undefined : label}
      ariaLabel={isLabelHidden === true ? label : undefined}
      value={value ?? undefined}
      withControls={false}
      min={0}
      elementBefore={
        <Text color="secondary" pl="150">
          $
        </Text>
      }
      elementAfter={elementAfter}
      isInvalid={isInvalid}
      isDisabled={isDisabled}
      onChange={(next) => onChange(next ?? null)}
    />
  );
}
